import { Dispatch, RefObject, SetStateAction, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiPaginationResponseInterface } from 'shared/types/api-pagination-response-interface'
import { AttachmentsRefInterface } from 'modules/attachments/components/context'
import { useAddComment } from 'modules/comments/api/add-comment'
import { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { useCommentsContext } from 'modules/comments/components/comments-list'
import { useCommentHighlight } from 'modules/comments/contexts/comment-highlight'
import { OnErrorType } from 'modules/comments/types'
import { isNewestFirst } from 'modules/comments/utils/comment-sorting'
import {
  isCommunityExtra,
  isLightLectureExtra,
} from 'modules/comments/utils/comments-extra-type-guards'
import {
  getLastCommentId,
  insertCommentToLinkedList,
} from 'modules/comments/utils/comments-linked-list'
import { communityKeys } from 'modules/community/api/key-factory'
import type { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'
import { PostAttachmentType } from 'modules/community/types/post-attachment'
import { PostCommentsInfoInterface } from 'modules/community/types/post-info'
import { escapeAttachmentLocalFields } from 'modules/community/utils/attachments'

const updateResponsesCount = (
  commentList: CommentInterface[],
  parentId: number,
): CommentInterface[] =>
  commentList.map(comment =>
    comment.id === parentId ? { ...comment, totalReplies: comment.totalReplies + 1 } : comment,
  )

interface UseCommentBoxResult {
  handleAddComment: (textHtml: string) => void
  attachmentsRef: RefObject<AttachmentsRefInterface>
  attachments: PostAttachmentType[]
  setAttachments: Dispatch<SetStateAction<PostAttachmentType[]>>
  initialHtml: string | undefined
  initialMentions: MentionInterface[]
}

export function useCommentBox({
  parentId,
  level = 0,
  setActiveComment = () => {},
  setOpenedResponses = () => {},
  onError,
  replyInfo,
}: {
  parentId: number | null
  level?: number
  setActiveComment?: (comment: CommentInterface | null) => void
  setOpenedResponses?: (value: boolean) => void
  onError?: OnErrorType
  replyInfo?: { userId: number; displayName: string; profileImage: string }
}): UseCommentBoxResult {
  const { setComments, commentsList, extra, repliesList, updateReplies, currentSortOption } =
    useCommentsContext()
  const { setHighlightedComment } = useCommentHighlight()
  const queryClient = useQueryClient()
  const parentIRI = parentId ? `/api/comment/comments/${parentId}` : null
  const attachmentsRef = useRef<AttachmentsRefInterface>(null)
  const [attachments, setAttachments] = useState<PostAttachmentType[]>([])

  const isCommunityComments = isCommunityExtra(extra)
  const isLightLecture = isLightLectureExtra(extra)

  const initialHtml: string | undefined = replyInfo
    ? `<p><@${replyInfo.userId}:${replyInfo.displayName}>, </p>`
    : undefined
  const initialMentions: MentionInterface[] = replyInfo
    ? [
        {
          userId: replyInfo.userId,
          displayName: replyInfo.displayName,
          profileImage: replyInfo.profileImage,
          positionStart: 0,
          positionEnd: 0,
        },
      ]
    : []

  const addCommentMutation = useAddComment({
    onSuccess: (data: CommentInterface) => {
      const commentWithFlag: CommentInterface = { ...data, __isNew: true, nextId: null }

      if (parentId === null) {
        setComments(prevComments =>
          insertCommentToLinkedList(
            prevComments,
            commentWithFlag,
            isNewestFirst(currentSortOption) ? null : getLastCommentId(prevComments),
          ),
        )
      } else {
        const updatedCommentList = updateResponsesCount(commentsList, parentId)
        setComments(updatedCommentList)
        if (level === 0) {
          setOpenedResponses(true)
        }

        updateReplies({
          commentId: parentId,
          replies: insertCommentToLinkedList(
            repliesList[parentId].replies,
            commentWithFlag,
            getLastCommentId(repliesList[parentId].replies),
          ),
        })
      }
      if (isCommunityComments) {
        setHighlightedComment(commentWithFlag)
      }
      setActiveComment(null)
      if (isCommunityComments) {
        void queryClient.invalidateQueries({
          queryKey: communityKeys.postsCommentsCount(extra.community.communityPath, [
            extra.community.postId,
          ]),
        })
        queryClient.setQueriesData<ApiPaginationResponseInterface<PostCommentsInfoInterface>>(
          { queryKey: communityKeys.postsCommentsGeneral() },
          oldData => {
            const info = oldData?.items.find(item => item.postId === extra.community.postId)

            if (!oldData || !info) return oldData

            return {
              ...oldData,
              items: oldData.items.map(item =>
                item.postId === extra.community.postId
                  ? {
                      ...item,
                      numberOfComments: item.numberOfComments + 1,
                    }
                  : item,
              ),
            }
          },
        )
        void queryClient.invalidateQueries({ queryKey: communityKeys.postsCommentsGeneral() })
      }
      attachmentsRef.current?.clear()
    },
    onError: (error: Error) => {
      onError?.(error)
    },
  })

  function handleAddComment(textHtml: string): void {
    const commentSource = isCommunityComments
      ? {
          commentSourceCommunityPost: {
            communityPostId: extra.community.postId,
          },
        }
      : isLightLecture
        ? {
            commentSourceLightLecture: {
              lectureId: extra.lightLecture.lectureId,
            },
          }
        : {
            commentSourcePage: {
              pageId: extra.course.pageId,
            },
          }
    const comment = {
      text: textHtml,
      parent: parentIRI,
      ...commentSource,
      attachments: escapeAttachmentLocalFields(attachments),
    }
    addCommentMutation.mutate(comment)
  }

  return {
    handleAddComment,
    attachmentsRef,
    attachments,
    setAttachments,
    initialHtml,
    initialMentions,
  }
}
